/**
 * @file useInactivityTimer.ts
 * @description Tracks user inactivity via mouse/keyboard/touch events.
 * After `timeoutMs` of silence it triggers auto-logout.
 * `warningMs` before auto-logout it sets `showWarning = true` so a modal
 * countdown can be displayed.
 *
 * @example
 * const { showWarning, extend, logout } = useInactivityTimer({
 *   timeoutMs: 30 * 60 * 1000,
 *   warningMs: 2 * 60 * 1000,
 * });
 */
import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

export interface UseInactivityTimerOptions {
  /** Total idle duration before auto-logout (ms). Default: 30 min */
  timeoutMs: number;
  /** How many ms before expiry to show the warning modal. Default: 2 min */
  warningMs?: number;
}

export interface UseInactivityTimerReturn {
  /** True when the warning countdown modal should be displayed */
  showWarning: boolean;
  /** Resets the inactivity timer (user clicked "Continue Working") */
  extend: () => void;
  /** Immediately logs the user out */
  logout: () => void;
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
] as const;

/**
 * useInactivityTimer
 * Monitors user activity events and manages session expiry logic.
 */
export function useInactivityTimer({
  timeoutMs,
  warningMs = 2 * 60 * 1000,
}: UseInactivityTimerOptions): UseInactivityTimerReturn {
  const auth = useContext(AuthContext);
  const [showWarning, setShowWarning] = useState(false);

  // Tracks setTimeout IDs so we can cancel them on activity
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Clears both scheduled timers. */
  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
  }, []);

  /** Called by activity events and "Continue Working" button. */
  const extend = useCallback(() => {
    setShowWarning(false);
    clearTimers();

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, timeoutMs - warningMs);

    logoutTimerRef.current = setTimeout(() => {
      auth?.logout();
    }, timeoutMs);
  }, [timeoutMs, warningMs, clearTimers, auth]);

  /** Immediately logs the user out and clears timers. */
  const logout = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    auth?.logout();
  }, [clearTimers, auth]);

  useEffect(() => {
    // Start the timers when the component mounts
    extend();

    // Throttle: only reset timers at most once per second to avoid excessive calls
    let lastReset = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      // Only reset if warning isn't showing yet (avoid resetting mid-countdown)
      setShowWarning((prev) => {
        if (!prev) extend();
        return prev;
      });
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutMs, warningMs]);

  return { showWarning, extend, logout };
}